// Net change per tracker over a set of change records.
//
// Rule: compare the state at the START of the set with the state at the END.
//   - start = the `before` of the EARLIEST record for that tracker
//     (null = the tracker was not in the plan)
//   - end   = the `after` of the LATEST record (history), or the tracker's
//     current active entry from the live plan (visit card). null = not active.
//
//   start null,    end present            -> "new"
//   start present, end present, different -> "changed" (frequency id or advice)
//   start present, end present, same      -> no net change (omitted)
//   start present, end null               -> "stopped"
//   start null,    end null               -> omitted (added then stopped)
//
// Intermediate records are ignored on purpose: adding, tweaking and removing
// within one visit nets out to what the clinician actually left behind.
import type {
  MonitoringPlanChange,
  MonitoringPlanEntry,
} from "@/types/monitoring";

export type NetChangeKind = "new" | "changed" | "stopped";

export interface NetChange {
  trackerId: number;
  trackerName: string;
  kind: NetChangeKind;
  /** State when the set started (null = not in plan). */
  before: MonitoringPlanEntry | null;
  /** State at the end (null = not active). */
  after: MonitoringPlanEntry | null;
}

export const normalizeAdvice = (advice: string | null | undefined) =>
  (advice ?? "").trim();

export const entriesDiffer = (a: MonitoringPlanEntry, b: MonitoringPlanEntry) =>
  (a.frequency?.id ?? null) !== (b.frequency?.id ?? null) ||
  normalizeAdvice(a.advice) !== normalizeAdvice(b.advice);

export const frequencyChanged = (
  before: MonitoringPlanEntry,
  after: MonitoringPlanEntry
) => (before.frequency?.id ?? null) !== (after.frequency?.id ?? null);

export const adviceChanged = (
  before: MonitoringPlanEntry,
  after: MonitoringPlanEntry
) => normalizeAdvice(before.advice) !== normalizeAdvice(after.advice);

/** Oldest first; equal timestamps fall back to reverse API order. */
const oldestFirst = (records: MonitoringPlanChange[]) =>
  records
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const d = a.r.created_at.localeCompare(b.r.created_at);
      return d !== 0 ? d : b.i - a.i;
    })
    .map((x) => x.r);

/**
 * State of each tracker when the set started: the earliest record's `before`.
 * Keeps an ongoing tracker's "since" date when a visit changed it and then put
 * it back (stop + undo creates a new entry that starts today).
 */
export const startStateByTracker = (records: MonitoringPlanChange[]) => {
  const start = new Map<number, MonitoringPlanEntry | null>();
  for (const rec of oldestFirst(records)) {
    if (!start.has(rec.tracker.id)) start.set(rec.tracker.id, rec.before);
  }
  return start;
};

/**
 * @param records change records, newest first (API order)
 * @param resolveAfter optional override for the end state of a tracker
 *   (e.g. the live plan); defaults to the latest record's `after`.
 */
export const computeNetChanges = (
  records: MonitoringPlanChange[],
  resolveAfter?: (trackerId: number) => MonitoringPlanEntry | null
): NetChange[] => {
  const ordered = oldestFirst(records);

  const byTracker = new Map<number, MonitoringPlanChange[]>();
  for (const rec of ordered) {
    const list = byTracker.get(rec.tracker.id) ?? [];
    list.push(rec);
    byTracker.set(rec.tracker.id, list);
  }

  const result: NetChange[] = [];
  for (const [trackerId, list] of byTracker) {
    const before = list[0].before;
    const after = resolveAfter
      ? resolveAfter(trackerId)
      : list[list.length - 1].after;
    const base = {
      trackerId,
      trackerName: list[0].tracker.name,
      before,
      after,
    };
    if (!before && after) result.push({ ...base, kind: "new" });
    else if (before && after) {
      if (entriesDiffer(before, after))
        result.push({ ...base, kind: "changed" });
    } else if (before && !after) result.push({ ...base, kind: "stopped" });
  }
  return result;
};
